import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useImperativeHandle, useReducer, useState, useCallback, useEffect } from 'react';
import classNames from '../../../node_modules/.pnpm/classnames@2.5.1/node_modules/classnames/index.js';
import { createPortal } from 'react-dom';
import { ReactComponent as ForwardRef } from '../../assets/icons/CloseIcon24.svg.js';
import { Button } from '../Button/Button.js';
import { Spinner } from '../Spinner/Spinner.js';
import style from './Modal.module.css.js';

const ScaleAnimation = {
    keyframes: [
        {
            transform: "scale(1)",
        },
        {
            transform: "scale(1.03)",
        },
    ],
    options: {
        duration: 75,
        iterations: 1,
    },
};
const modalReducer = (state, action) => {
    switch (action.type) {
        case "OPEN":
            if (state.status === "hidden") {
                return { status: "opening" };
            }
            return state;
        case "CLOSE":
            if (state.status === "visible") {
                return { status: "closing" };
            }
            return state;
        case "FINISH_OPENING":
            if (state.status === "opening") {
                return { status: "visible" };
            }
            return state;
        case "FINISH_CLOSING":
            if (state.status === "closing") {
                return { status: "hidden" };
            }
            return state;
        default:
            return state;
    }
};
const Modal = forwardRef(({ children, footer, className, size = "medium", title, persistent = false, preventCloseOnEscape = false, isLoading = false, open, onClose, onCloseStart, onOpen, background, }, ref) => {
    const modalRef = useRef(null);
    useImperativeHandle(ref, () => modalRef.current);
    const contentRef = useRef(null);
    const [modalState, dispatch] = useReducer(modalReducer, {
        status: open ? "visible" : "hidden",
    });
    const [isContentOverflowing, setIsContentOverflowing] = useState(false);
    const disableScroll = () => {
        const scrollbarWidth = window.innerWidth - document.body.clientWidth;
        document.body.style.overflow = "hidden";
        document.body.style.paddingRight = `${scrollbarWidth}px`;
    };
    const enableScroll = () => {
        document.body.style.overflow = "auto";
        document.body.style.paddingRight = "0";
    };
    const handleClose = useCallback(() => {
        if (modalState.status === "visible" || modalState.status === "opening") {
            onCloseStart?.();
            dispatch({ type: "CLOSE" });
        }
    }, [modalState.status, onCloseStart]);
    const handleAnimationEnd = useCallback(() => {
        switch (modalState.status) {
            case "opening":
                dispatch({ type: "FINISH_OPENING" });
                onOpen?.();
                break;
            case "closing":
                dispatch({ type: "FINISH_CLOSING" });
                onClose?.();
                break;
        }
    }, [modalState.status, onOpen, onClose]);
    useEffect(() => {
        if (modalState.status === "hidden") {
            enableScroll();
        }
        else {
            disableScroll();
        }
    }, [modalState.status]);
    useEffect(() => {
        if (open) {
            dispatch({ type: "OPEN" });
        }
        else {
            handleClose();
        }
    }, [handleClose, open]);
    useEffect(() => {
        const checkOverflow = () => {
            if (contentRef.current) {
                const hasOverflow = contentRef.current.scrollHeight > contentRef.current.clientHeight;
                setIsContentOverflowing(hasOverflow);
            }
        };
        if (contentRef.current) {
            const resizeObserver = new ResizeObserver(checkOverflow);
            resizeObserver.observe(contentRef.current);
            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [children]);
    useEffect(() => {
        if (modalState.status === "visible" &&
            modalRef.current &&
            !modalRef.current.contains(document.activeElement)) {
            modalRef.current.focus();
        }
    }, [modalState.status]);
    useEffect(() => {
        const handleKeyDown = (event) => {
            if ((event.key !== "Escape" && event.key !== "Esc") || !open) {
                return;
            }
            if (modalRef.current && modalRef.current.contains(document.activeElement)) {
                event.stopPropagation();
                if (!preventCloseOnEscape) {
                    handleClose();
                }
                else {
                    modalRef.current?.animate(ScaleAnimation.keyframes, ScaleAnimation.options);
                }
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleClose, open, preventCloseOnEscape]);
    return (jsx(Fragment, { children: modalState.status !== "hidden" &&
            createPortal(jsxs("div", { className: style.wrapper, "data-state": modalState.status, children: [jsx("div", { className: style.overlay, "data-state": modalState.status, onClick: () => {
                            if (!persistent)
                                handleClose();
                        } }), jsxs("div", { className: classNames(className, style.modal, style[size]), "data-state": modalState.status, onAnimationEnd: handleAnimationEnd, role: "dialog", "aria-modal": "true", ref: modalRef, tabIndex: -1, children: [background && jsx("div", { className: style.background, children: background }), jsxs("div", { className: style.header, children: [title && jsx("div", { className: style.title, children: title }), !persistent && (jsx(Button, { className: style.close, onClick: () => {
                                            handleClose();
                                        }, variant: "borderless", children: jsx(ForwardRef, {}) }))] }), isLoading && (jsx("div", { className: style.loadingContent, children: jsx(Spinner, { size: "sm" }) })), !isLoading && (jsx("div", { className: classNames(style.content, !title && style.fullHeightContent), children: children })), footer && (jsx("div", { className: classNames(style.footer, isContentOverflowing && style.footerWithBorder), children: footer }))] })] }), document.getElementById("modal-root") ?? document.body) }));
});
Modal.displayName = "Modal";

export { Modal };
//# sourceMappingURL=Modal.js.map
